import { useState, useEffect, useCallback } from "react";
import { API } from '../config/api';
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import { Shield, Download, AlertTriangle, CheckCircle, Clock, Users, TrendingUp, ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { Button } from "../components/ui/button";

const STATUS_COLORS = {
  clear: { bg: "bg-teal-dim/30", text: "text-teal", border: "border-teal/50", label: "Clear" },
  clear_with_conditions: { bg: "bg-status-amber-dim/30", text: "text-status-amber", border: "border-amber-700/50", label: "Conditional" },
  conflict: { bg: "bg-status-red-dim/30", text: "text-status-red", border: "border-red-700/50", label: "Conflict" },
  not_ready: { bg: "bg-slate-800/50", text: "text-slate-400", border: "border-slate-600/50", label: "Not Ready" },
  na: { bg: "bg-blue-900/30", text: "text-blue-400", border: "border-blue-700/50", label: "N/A" },
  do_not_act: { bg: "bg-status-red-dim/50", text: "text-status-red", border: "border-red-600/50", label: "Do Not Act" },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || { bg: "bg-slate-800", text: "text-slate-400", border: "border-slate-600", label: status };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${s.bg} ${s.text} border ${s.border}`}>
      {s.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = "emerald" }) {
  const colors = {
    emerald: "from-teal-dim/40 to-teal-dim/10 border-teal/30",
    amber: "from-amber-900/40 to-amber-900/10 border-amber-700/30",
    red: "from-red-900/40 to-red-900/10 border-red-700/30",
    blue: "from-blue-900/40 to-blue-900/10 border-blue-700/30",
    slate: "from-slate-800/60 to-slate-800/20 border-slate-600/30",
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-md p-4`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-slate-400" />
        <span className="text-xs text-slate-400 font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function downloadCSV(data, filename) {
  if (!data || data.length === 0) return;
  const headers = Object.keys(data[0]);
  const csv = [
    headers.join(","),
    ...data.map((row) =>
      headers.map((h) => {
        const val = String(row[h] || "").replace(/"/g, '""');
        return `"${val}"`;
      }).join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ConflictCheckHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAffected, setShowAffected] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAllChecks, setShowAllChecks] = useState(false);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const userId = sessionStorage.getItem("bonsai_user_id");
      const resp = await axios.get(`${API}/conflict-checks/health`, {
        headers: { "X-Bonsai-User-Id": userId },
      });
      setData(resp.data);
    } catch (e) {
      setError(errorMessage(e, "Failed to load health data"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  if (loading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="animate-pulse text-slate-400 text-sm">Loading conflict check health data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="text-status-red text-sm">{error}</div>
      </div>
    );
  }

  const { summary, monthly_trends, fee_earner_breakdown, potentially_affected, all_checks } = data;
  const statusBreakdown = summary.status_breakdown || {};

  // Filter all checks for the table
  const filteredChecks = (all_checks || []).filter((cc) => {
    const matchStatus = filterStatus === "all" || cc.conflict_status === filterStatus;
    const matchSearch = !searchTerm || 
      (cc.client_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cc.provisional_id || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cc.matter_reference || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cc.decision_maker_name || "").toLowerCase().includes(searchTerm.toLowerCase());
    return matchStatus && matchSearch;
  });

  return (
    <div className="min-h-screen bg-navy" data-testid="conflict-health-dashboard">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-navy">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-teal-dim/30 border border-teal/40">
              <Shield className="w-5 h-5 text-teal" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-white">Conflict Check Health</h1>
              <p className="text-xs text-slate-500">Overview of all conflict checks across the firm</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => downloadCSV(all_checks, `conflict-checks-${new Date().toISOString().slice(0,10)}.csv`)}
              className="bg-teal hover:bg-teal-hover text-white text-xs h-8 gap-1.5"
              data-testid="download-all-csv"
            >
              <Download className="w-3.5 h-3.5" /> Download All (CSV)
            </Button>
            {potentially_affected.length > 0 && (
              <Button
                onClick={() => downloadCSV(potentially_affected, `affected-checks-${new Date().toISOString().slice(0,10)}.csv`)}
                variant="outline"
                className="border-amber-700/50 text-status-amber hover:bg-status-amber-dim/20 text-xs h-8 gap-1.5"
                data-testid="download-affected-csv"
              >
                <AlertTriangle className="w-3.5 h-3.5" /> Download Flagged ({potentially_affected.length})
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard icon={Shield} label="Total Checks" value={summary.total} color="slate" />
          <StatCard icon={CheckCircle} label="Clearance Rate" value={`${summary.clearance_rate}%`} sub={`${statusBreakdown.clear || 0} clear + ${statusBreakdown.clear_with_conditions || 0} conditional`} color="emerald" />
          <StatCard icon={Clock} label="Avg Resolution" value={summary.avg_resolution_hours ? `${summary.avg_resolution_hours}h` : "N/A"} color="blue" />
          <StatCard icon={AlertTriangle} label="Flagged for Review" value={summary.potentially_affected_count} sub="Cleared with empty hits" color={summary.potentially_affected_count > 0 ? "amber" : "emerald"} />
          <StatCard icon={TrendingUp} label="Conflicts Found" value={statusBreakdown.conflict || 0} color={statusBreakdown.conflict > 0 ? "red" : "emerald"} />
        </div>

        {/* Status Breakdown Bar */}
        <div className="bg-navy border border-slate-700/40 rounded-md p-4">
          <h3 className="text-xs font-medium text-slate-400 mb-3">Status Distribution</h3>
          <div className="flex rounded-md overflow-hidden h-6">
            {Object.entries(statusBreakdown).map(([status, count]) => {
              const pct = (count / summary.total * 100);
              if (pct < 1) return null;
              const s = STATUS_COLORS[status] || { bg: "bg-slate-700", text: "text-slate-300" };
              return (
                <div key={status} className={`${s.bg} flex items-center justify-center relative group`} style={{ width: `${pct}%` }}
                  data-testid={`status-bar-${status}`}>
                  {pct > 8 && <span className={`text-[10px] font-medium ${s.text}`}>{s.label || status} ({count})</span>}
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-600 rounded px-2 py-0.5 text-[10px] text-white hidden group-hover:block whitespace-nowrap z-10">
                    {s.label || status}: {count} ({pct.toFixed(1)}%)
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Monthly Trends + Fee Earner side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Monthly Trends */}
          <div className="bg-navy border border-slate-700/40 rounded-md p-4">
            <h3 className="text-xs font-medium text-slate-400 mb-3">Monthly Trends</h3>
            <div className="space-y-2">
              {monthly_trends.map((m) => {
                const max = Math.max(...monthly_trends.map(t => t.total), 1);
                return (
                  <div key={m.month} className="flex items-center gap-3" data-testid={`month-${m.month}`}>
                    <span className="text-xs text-slate-500 w-16 shrink-0">{m.month}</span>
                    <div className="flex-1 flex h-5 rounded overflow-hidden bg-slate-800/50">
                      <div className="bg-teal-dim/60 transition-all" style={{ width: `${(m.clear / max * 100)}%` }} />
                      <div className="bg-status-red-dim/60 transition-all" style={{ width: `${(m.conflict / max * 100)}%` }} />
                      <div className="bg-slate-700/60 transition-all" style={{ width: `${(m.other / max * 100)}%` }} />
                    </div>
                    <span className="text-xs text-slate-400 w-8 text-right">{m.total}</span>
                  </div>
                );
              })}
              {monthly_trends.length === 0 && <p className="text-xs text-slate-500">No data available</p>}
            </div>
            <div className="flex gap-4 mt-3 text-[10px] text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-teal" /> Clear</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-status-red" /> Conflict</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-slate-600" /> Other</span>
            </div>
          </div>

          {/* Fee Earner Breakdown */}
          <div className="bg-navy border border-slate-700/40 rounded-md p-4">
            <h3 className="text-xs font-medium text-slate-400 mb-3 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Decision Maker Breakdown
            </h3>
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {fee_earner_breakdown.map((fe) => (
                <div key={fe.name} className="flex items-center justify-between py-1.5 border-b border-slate-800/50 last:border-0" data-testid={`fe-${fe.name}`}>
                  <span className="text-xs text-txt-muted truncate max-w-[140px]">{fe.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-teal">{fe.clear} clear</span>
                    {fe.conflict > 0 && <span className="text-[10px] text-status-red">{fe.conflict} conflict</span>}
                    {fe.pending > 0 && <span className="text-[10px] text-slate-500">{fe.pending} other</span>}
                    <span className="text-[10px] text-slate-400 font-medium w-6 text-right">{fe.total}</span>
                  </div>
                </div>
              ))}
              {fee_earner_breakdown.length === 0 && <p className="text-xs text-slate-500">No data available</p>}
            </div>
          </div>
        </div>

        {/* Flagged for Review Section */}
        {potentially_affected.length > 0 && (
          <div className="bg-navy border border-amber-700/30 rounded-md" data-testid="flagged-section">
            <button
              onClick={() => setShowAffected(!showAffected)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-status-amber" />
                <span className="text-sm font-medium text-status-amber">
                  {potentially_affected.length} Checks Flagged for Review
                </span>
                <span className="text-[10px] text-slate-500">Cleared with no/empty search hits</span>
              </div>
              {showAffected ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {showAffected && (
              <div className="border-t border-amber-700/20 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-500 border-b border-slate-700/50">
                      <th className="text-left px-4 py-2 font-medium">Client</th>
                      <th className="text-left px-4 py-2 font-medium">Matter Ref</th>
                      <th className="text-left px-4 py-2 font-medium">Status</th>
                      <th className="text-left px-4 py-2 font-medium">Decision Maker</th>
                      <th className="text-left px-4 py-2 font-medium">Date</th>
                      <th className="text-left px-4 py-2 font-medium">Hits Summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {potentially_affected.map((cc, i) => (
                      <tr key={i} className="border-b border-slate-800/30 hover:bg-status-amber-dim/10">
                        <td className="px-4 py-2 text-txt">{cc.client_name}</td>
                        <td className="px-4 py-2 text-slate-400">{cc.matter_reference || cc.provisional_id}</td>
                        <td className="px-4 py-2"><StatusBadge status={cc.conflict_status} /></td>
                        <td className="px-4 py-2 text-slate-400">{cc.decision_maker_name}</td>
                        <td className="px-4 py-2 text-slate-500">{cc.created_at}</td>
                        <td className="px-4 py-2 text-slate-500 max-w-[200px] truncate">{cc.hits_summary || "Empty"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* All Checks Table */}
        <div className="bg-navy border border-slate-700/40 rounded-md" data-testid="all-checks-section">
          <button
            onClick={() => setShowAllChecks(!showAllChecks)}
            className="w-full flex items-center justify-between px-4 py-3 text-left"
          >
            <span className="text-sm font-medium text-txt-muted">All Conflict Checks ({all_checks.length})</span>
            {showAllChecks ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
          </button>
          {showAllChecks && (
            <div className="border-t border-slate-700/30">
              {/* Filter bar */}
              <div className="px-4 py-2 flex gap-2 items-center border-b border-slate-800/50">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Search by name, ID, or reference..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full text-xs pl-8 pr-7 py-1.5 rounded border border-slate-700 bg-slate-800/50 text-txt placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-teal/40"
                    data-testid="checks-search-input"
                  />
                  {searchTerm && (
                    <button onClick={() => setSearchTerm("")} className="absolute right-2 top-1/2 -translate-y-1/2">
                      <X className="w-3 h-3 text-slate-500 hover:text-slate-300" />
                    </button>
                  )}
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="text-xs px-2 py-1.5 rounded border border-slate-700 bg-slate-800/50 text-slate-300 focus:outline-none"
                  data-testid="checks-status-filter"
                >
                  <option value="all">All statuses</option>
                  <option value="clear">Clear</option>
                  <option value="clear_with_conditions">Conditional</option>
                  <option value="conflict">Conflict</option>
                  <option value="not_ready">Not Ready</option>
                  <option value="na">N/A</option>
                  <option value="do_not_act">Do Not Act</option>
                </select>
                <span className="text-[10px] text-slate-500">{filteredChecks.length} results</span>
              </div>

              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-navy">
                    <tr className="text-slate-500 border-b border-slate-700/50">
                      <th className="text-left px-4 py-2 font-medium">Client</th>
                      <th className="text-left px-4 py-2 font-medium">Matter Ref</th>
                      <th className="text-left px-4 py-2 font-medium">Matter Ref</th>
                      <th className="text-left px-4 py-2 font-medium">Status</th>
                      <th className="text-left px-4 py-2 font-medium">Decision Maker</th>
                      <th className="text-left px-4 py-2 font-medium">Date</th>
                      <th className="text-left px-4 py-2 font-medium">Hits Summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredChecks.slice(0, 100).map((cc, i) => (
                      <tr key={i} className="border-b border-slate-800/30 hover:bg-slate-800/30">
                        <td className="px-4 py-2 text-txt">{cc.client_name}</td>
                        <td className="px-4 py-2 text-slate-400">{cc.matter_reference || cc.provisional_id}</td>
                        <td className="px-4 py-2 text-slate-400">{cc.matter_reference || "-"}</td>
                        <td className="px-4 py-2"><StatusBadge status={cc.conflict_status} /></td>
                        <td className="px-4 py-2 text-slate-400">{cc.decision_maker_name}</td>
                        <td className="px-4 py-2 text-slate-500">{cc.created_at}</td>
                        <td className="px-4 py-2 text-slate-500 max-w-[200px] truncate">{cc.hits_summary || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredChecks.length > 100 && (
                  <p className="text-xs text-slate-500 px-4 py-2">Showing first 100 of {filteredChecks.length}. Download CSV for full list.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
